import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {FiEdit2, FiMail, FiPhone, FiSave, FiUser, FiX} from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../api/auth';
import { useAuth } from '../context/AuthContext';
import { formatUKPhoneNumber } from '../utils/phoneFormat';
import { ordersAPI, OrderResponse } from '../api/orders';
import { formatCurrency } from '../utils/currency';
import { getErrorDetail } from '../utils/errorDetail';

const Container = styled.div`
  min-height: calc(100vh - var(--header-height));
  padding: 2rem 1rem;
  max-width: 800px;
  margin: 0 auto;
  margin-top: var(--header-height);

  @media (min-width: 768px) {
    padding: 3rem 2rem;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  margin-bottom: 2rem;
  color: var(--text-color);
`;

const Card = styled.div`
  background: var(--white);
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

const CardTitle = styled.h2`
  font-size: 1.25rem;
  margin-bottom: 1.5rem;
  color: var(--text-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const InfoGrid = styled.div`
  display: grid;
  gap: 1.5rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
`;

const InfoIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background-color: var(--primary-light);
  border-radius: 8px;
  color: var(--primary-color);
  flex-shrink: 0;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const InfoContent = styled.div`
  flex: 1;
`;

const InfoLabel = styled.div`
  font-size: 0.875rem;
  color: var(--gray-dark);
  margin-bottom: 0.25rem;
`;

const InfoValue = styled.div`
  font-size: 1rem;
  color: var(--text-color);
  font-weight: 500;
`;

const EditButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background-color: var(--primary-color);
  color: var(--white);
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.875rem;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--primary-dark);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  color: var(--gray-dark);
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid var(--gray-light);
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const SaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background-color: var(--primary-color);
  color: var(--white);
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--primary-dark);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const CancelButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background-color: var(--gray-light);
  color: var(--text-color);
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--gray-dark);
    color: var(--white);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const ErrorMessage = styled.div`
  color: var(--error-color);
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const SuccessMessage = styled.div`
  color: var(--success-color);
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const AccountStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: var(--white);
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: var(--primary-color);
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: var(--gray-dark);
`;

const AccountPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, user, checkAuth } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [orderStats, setOrderStats] = useState({
    totalOrders: 0,
    activeOrders: 0,
    totalSpent: 0
  });

  // Immediately redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
    } else {
      checkAuth();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || ''
      });
    }

    // Fetch order statistics
    const fetchOrderStats = async () => {
      try {
        const orders = await ordersAPI.getUserOrders();

        // Calculate statistics
        const totalOrders = orders.length;
        const activeOrders = orders.filter(order =>
          ['pending', 'confirmed', 'ready'].includes(order.status.toLowerCase())
        ).length;
        const totalSpent = orders.reduce((sum, order) => sum + order.total_amount, 0);

        setOrderStats({
          totalOrders,
          activeOrders,
          totalSpent
        });
      } catch (error) {
        console.error('Failed to fetch order statistics:', error);
      }
    };

    fetchOrderStats();
  }, [isAuthenticated, user]);

  const handleEdit = () => {
    setIsEditing(true);
    setError('');
    setSuccess('');
  };

  const handleCancel = () => {
    setIsEditing(false);
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || ''
      });
    }
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      // Call backend API to update user profile
      const updatedUser = await authAPI.updateCurrentUser({
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone
      });

      // Don't store user data in localStorage for security
      // Refresh auth context to get updated user data from API
      await checkAuth();

      setIsEditing(false);
      setSuccess(t('account.updateSuccessMessage'));

      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err: any) {
      console.error('Update error:', err);
      setError(getErrorDetail(err) || t('account.updateErrorFallback'));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handlePhoneBlur = () => {
    // Format phone number when user leaves the field
    if (formData.phone) {
      const formatted = formatUKPhoneNumber(formData.phone);
      setFormData(prev => ({
        ...prev,
        phone: formatted
      }));
    }
  };

  // Don't render anything if not authenticated or no user data
  if (!isAuthenticated || !user) {
    return null;
  }

  return (
    <Container>
      <Title>{t('account.pageTitle')}</Title>

      <AccountStats>
        <StatCard>
          <StatValue>{user?.loyalty_points_balance ?? 0}</StatValue>
          <StatLabel>{t('account.loyaltyPointsLabel')}</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{orderStats.totalOrders}</StatValue>
          <StatLabel>{t('account.totalOrdersLabel')}</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{orderStats.activeOrders}</StatValue>
          <StatLabel>{t('account.activeOrdersLabel')}</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{formatCurrency(orderStats.totalSpent)}</StatValue>
          <StatLabel>{t('account.totalSpentLabel')}</StatLabel>
        </StatCard>
      </AccountStats>

      <Card>
        <CardTitle>
          {t('account.personalInfoTitle')}
          {!isEditing && (
            <EditButton onClick={handleEdit}>
              <FiEdit2 />
              {t('account.editButton')}
            </EditButton>
          )}
        </CardTitle>

        {!isEditing ? (
          <>
            <InfoGrid>
              <InfoItem>
                <InfoIcon>
                  <FiUser />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>{t('account.fullNameLabel')}</InfoLabel>
                  <InfoValue>
                    {user?.first_name && user?.last_name
                      ? `${user.first_name} ${user.last_name}`
                      : t('account.notSetValue')}
                  </InfoValue>
                </InfoContent>
              </InfoItem>

              <InfoItem>
                <InfoIcon>
                  <FiMail />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>{t('account.emailLabel')}</InfoLabel>
                  <InfoValue>{user?.email || t('account.notSetValue')}</InfoValue>
                </InfoContent>
              </InfoItem>

              <InfoItem>
                <InfoIcon>
                  <FiPhone />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>{t('account.phoneLabel')}</InfoLabel>
                  <InfoValue>{user?.phone || t('account.notSetValue')}</InfoValue>
                </InfoContent>
              </InfoItem>
            </InfoGrid>
            {success && <SuccessMessage>{success}</SuccessMessage>}
          </>
        ) : (
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label>{t('account.emailCannotBeChangedLabel')}</Label>
              <Input
                type="email"
                value={user?.email || ''}
                disabled
                style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="first_name">{t('account.firstNameLabel')}</Label>
              <Input
                type="text"
                id="first_name"
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="last_name">{t('account.lastNameLabel')}</Label>
              <Input
                type="text"
                id="last_name"
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="phone">{t('account.phoneLabel')}</Label>
              <Input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                onBlur={handlePhoneBlur}
                placeholder={t('account.phonePlaceholder')}
                maxLength={20}
              />
            </FormGroup>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <ButtonGroup>
              <SaveButton type="submit">
                <FiSave />
                {t('account.saveChangesButton')}
              </SaveButton>
              <CancelButton type="button" onClick={handleCancel}>
                <FiX />
                {t('account.cancelButton')}
              </CancelButton>
            </ButtonGroup>
          </Form>
        )}
      </Card>

      <Card>
        <CardTitle>{t('account.securityTitle')}</CardTitle>
        <EditButton onClick={() => alert(t('account.changePasswordComingSoonAlert'))}>
          <FiEdit2 />
          {t('account.changePasswordButton')}
        </EditButton>
      </Card>
    </Container>
  );
};

export default AccountPage;